import React from "react";
import { AlertTriangle, Info } from "lucide-react";

/**
 * NutritionOverview — Clean-Wellness themed macro summary card.
 *
 * Rendered inline inside the CartIQ Ask thread when the user's question
 * matches a macro/nutrition intent (backend classifier decides). Powered by
 * the `nutrition_summary` aggregate produced by `services/nutrition.py`.
 *
 * Fields consumed:
 *   proteins_g · carbs_g · sugars_g · fat_g
 *   items_with_data · items_total · coverage_pct
 *   anomalies: [{item, flags: string[], category}]
 *
 * Design decisions:
 * - Pure inline SVG bars, no chart lib — keeps the ask thread bundle lean.
 * - Coverage warning shown when <60% of items had OFF matches so users
 *   don't over-index on a partial picture.
 * - Anomalies capped at 3 in the UI (backend already caps at 5).
 * - Wrapped in `.cartiq-wellness` scope so the card renders in the light
 *   health-green palette even inside the dark ask drawer.
 */

const MACRO_COLORS = {
  protein: "#3B6D11", // dark green
  carbs:   "#FF6B00", // saffron
  sugar:   "#E11D48", // soft-coral / warn
  fat:     "#0C447C", // navy
};

const fmtG = (n) => `${Math.round(Number(n || 0))}g`;

export default function NutritionOverview({ data }) {
  if (!data || (data.items_with_data || 0) === 0) return null;

  const protein = Number(data.proteins_g || 0);
  const carbs = Number(data.carbs_g || 0);
  const sugars = Number(data.sugars_g || 0);
  const fat = Number(data.fat_g || 0);
  const grams = Math.max(1, Number(data.total_grams_estimated || 0));
  const coverage = Math.max(0, Math.min(100, Number(data.coverage_pct || 0)));
  const anomalies = (data.anomalies || []).slice(0, 3);

  // Normalise the macro bar row to the LARGEST bar in the set so smaller
  // bars still have visible width. Prevents the "sugar looks like 0px" bug.
  const maxMacro = Math.max(protein, carbs, sugars, fat, 1);
  const pctOf = (v) => Math.round((v / maxMacro) * 100);

  return (
    <div
      className="cartiq-wellness rounded-2xl overflow-hidden mt-2"
      style={{
        background: "#FFFFFF",
        border: "1px solid #C0DD97",
        boxShadow: "0 4px 12px rgba(59,109,17,0.08)",
      }}
      data-testid="nutrition-overview-card"
      role="region"
      aria-label="Nutrition overview"
    >
      {/* Header strip */}
      <div
        className="flex items-center justify-between px-4 py-2.5"
        style={{ background: "#EAF3DE", borderBottom: "1px solid #D4EAD0" }}
      >
        <div>
          <div
            className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#639922]"
            data-testid="nutrition-overview-title"
          >
            Your macro split
          </div>
          <div className="text-[11px] text-[#3B6D11] font-medium">
            Across {data.items_with_data} of {data.items_total} items ·{" "}
            {Math.round(grams / 100) / 10} kg estimated
          </div>
        </div>
        {coverage < 60 && (
          <div
            className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.14em] font-bold px-2 py-1 rounded-full"
            style={{ background: "#FFFFFF", border: "1px solid #C0DD97", color: "#639922" }}
            data-testid="nutrition-coverage-warn"
            title="We matched fewer than 60% of your items — the picture is directional."
          >
            <Info className="w-3 h-3" />
            {coverage}% matched
          </div>
        )}
      </div>

      {/* Macro bars */}
      <div className="px-4 py-4 space-y-2.5" data-testid="nutrition-macro-list">
        <MacroBar label="Protein"       value={protein} color={MACRO_COLORS.protein} pct={pctOf(protein)} />
        <MacroBar label="Carbs"         value={carbs}   color={MACRO_COLORS.carbs}   pct={pctOf(carbs)}   />
        <MacroBar label="Sugar"         value={sugars}  color={MACRO_COLORS.sugar}   pct={pctOf(sugars)} warn={sugars >= carbs * 0.5 && sugars > 30} />
        <MacroBar label="Fat"           value={fat}     color={MACRO_COLORS.fat}     pct={pctOf(fat)}     />
      </div>

      {/* Anomaly callouts */}
      {anomalies.length > 0 && (
        <div
          className="px-4 py-3 space-y-1.5"
          style={{ background: "#FEF3F2", borderTop: "1px solid #FCA5A5" }}
          data-testid="nutrition-anomaly-list"
        >
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-[#9F1239]">
            <AlertTriangle className="w-3 h-3" />
            Watchlist
          </div>
          {anomalies.map((a, i) => (
            <div
              key={i}
              className="text-[12px] text-[#7F1D1D] leading-snug"
              data-testid={`nutrition-anomaly-${i}`}
            >
              <span className="font-bold text-[#9F1239]">{a.item}</span>
              <span className="text-[#B91C1C]"> — {a.flags.join(" · ")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sub-component ───────────────────────────────────────────────────────────
function MacroBar({ label, value, color, pct, warn }) {
  const testid = `nutrition-bar-${label.toLowerCase()}`;
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="w-14 shrink-0 text-[11px] font-bold uppercase tracking-[0.14em] text-[#3B6D11]">
        {label}
      </div>
      <div
        className="flex-1 h-2.5 rounded-full overflow-hidden"
        style={{ background: "#F4FAF2", border: "1px solid #D4EAD0" }}
      >
        <div
          className="h-full transition-all"
          style={{
            width: `${Math.max(pct, 2)}%`,
            background: color,
            boxShadow: warn ? `0 0 0 2px rgba(225,29,72,0.22)` : "none",
          }}
        />
      </div>
      <div
        className="w-16 shrink-0 text-right font-mono-num text-[12px] font-extrabold"
        style={{ color }}
      >
        {fmtG(value)}
      </div>
    </div>
  );
}
